import React, { useEffect, useRef, useState } from 'react';
import { Check, Box, Trash2, ArrowRight, Save, Hash } from 'lucide-react';
import { getTheme } from './themeUtils';
import { ScanMode, SupplierProduct } from '../types';
import { PricingInput } from '../PricingInput';

interface ProductCardProps {
  lastScanned: string | null;
  isKnown: boolean;
  lastItemDetails: SupplierProduct | null;
  lastItemQty: number;
  scanMode: ScanMode;
  transactionQty: number;
  newName: string;
  newPrice: string; // Legacy: kept for backward compatibility
  newSellingPrice: string; // Legacy: kept for backward compatibility
  // New hybrid pricing fields
  newPurchaseNet?: number;
  newPurchaseGross?: number;
  newSellingNet?: number;
  newSellingGross?: number;
  newVatRate?: number;
  newMinQuantity: string;
  newIsWeighable: boolean;
  newUnitWeight: string;
  newUnit?: 'db' | 'm' | 'kg';
  nameSuggestions: string[];
  showSuggestions: boolean;
  nameInputRef: React.RefObject<HTMLInputElement>;
  qtyInputRef: React.RefObject<HTMLInputElement>;
  onNameChange: (value: string) => void;
  onPriceChange: (value: string) => void; // Legacy callback
  onSellingPriceChange: (value: string) => void; // Legacy callback
  // New pricing callbacks
  onPurchaseNetChange?: (value: number) => void;
  onPurchaseGrossChange?: (value: number) => void;
  onSellingNetChange?: (value: number) => void;
  onSellingGrossChange?: (value: number) => void;
  onVatRateChange?: (value: number) => void;
  onMinQuantityChange: (value: string) => void;
  onIsWeighableChange: (value: boolean) => void;
  onUnitWeightChange: (value: string) => void;
  onUnitChange?: (value: 'db' | 'm' | 'kg') => void;
  onQtyChange: (value: string) => void;
  onSave: () => void;
  onApprove: () => void;
  onDiscard: () => void;
  onGenerateInternalEan: () => void;
  onSuggestionSelect: (suggestion: string) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  lastScanned,
  isKnown,
  lastItemDetails,
  lastItemQty,
  scanMode,
  transactionQty,
  newName,
  newPrice: _newPrice,
  newSellingPrice: _newSellingPrice,
  newPurchaseNet,
  newPurchaseGross,
  newSellingNet,
  newSellingGross,
  newVatRate,
  newMinQuantity,
  newIsWeighable,
  newUnitWeight,
  newUnit = 'db',
  nameSuggestions,
  showSuggestions,
  nameInputRef,
  qtyInputRef,
  onNameChange,
  onPriceChange: _onPriceChange,
  onSellingPriceChange: _onSellingPriceChange,
  onPurchaseNetChange,
  onPurchaseGrossChange,
  onSellingNetChange,
  onSellingGrossChange,
  onVatRateChange,
  onMinQuantityChange,
  onIsWeighableChange,
  onUnitWeightChange,
  onUnitChange,
  onQtyChange,
  onSave,
  onApprove,
  onDiscard,
  onGenerateInternalEan,
  onSuggestionSelect,
}) => {
  if (!lastScanned) return null;

  const theme = getTheme(scanMode);

  // Helper variable for dynamic unit display - no hardcoded "db" text
  const currentUnit = (isKnown ? lastItemDetails?.unit : newUnit) || 'db';
  const currentUnitLower = (currentUnit ?? '').toString().toLowerCase();

  // Local string state for the quantity input so user can freely type without "0" snapping back.
  // Used for ALL unit types (db, kg, m) — not just decimal units.
  const isDecimalUnit = currentUnitLower === 'kg' || currentUnitLower === 'm';
  const [quantityInputStr, setQuantityInputStr] = useState(() =>
    transactionQty === 0 ? '' : String(transactionQty)
  );

  // Track whether the user is actively typing in the quantity input.
  // While typing, we skip syncing from parent to avoid overwriting user input.
  const isUserTypingQtyRef = useRef(false);
  const userTypingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync from parent when product changes (lastScanned) — always reset.
  useEffect(() => {
    isUserTypingQtyRef.current = false;
    setQuantityInputStr(transactionQty === 0 ? '' : String(transactionQty));
  }, [lastScanned]);

  // Also sync when transactionQty changes externally (e.g. repeated scans of the same product,
  // +/- buttons, or parent state updates) — but NOT while user is actively typing.
  useEffect(() => {
    if (!isUserTypingQtyRef.current) {
      setQuantityInputStr(transactionQty === 0 ? '' : String(transactionQty));
    }
  }, [transactionQty]);

  // Auto-focus quantity input if unit is 'm' or 'kg'
  useEffect(() => {
    if (isDecimalUnit && qtyInputRef.current) {
      // Small delay to ensure the card is rendered
      const timer = setTimeout(() => {
        qtyInputRef.current?.focus();
        qtyInputRef.current?.select();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [lastScanned, currentUnit, qtyInputRef]);

  return (
    <div
      className={`rounded-xl shadow-2xl border-l-8 overflow-hidden animate-in zoom-in duration-200 bg-card border-gray-200 dark:border-gray-700 ${
        isKnown
          ? `border-l-${theme.color}-500 dark:border-l-${theme.color}-400`
          : 'border-l-amber-500 dark:border-l-amber-400'
      }`}
    >
      <div className="p-6">
        <div className="flex flex-col md:flex-row justify-between items-start mb-6 gap-4">
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wider opacity-60 mb-2 flex items-center gap-1 dark:opacity-80">
              {isKnown ? (
                <Check size={14} className={theme.text} />
              ) : (
                <Box size={14} className="text-amber-600 dark:text-amber-400" />
              )}
              {isKnown ? 'Sikeres azonosítás' : 'ISMERETLEN TERMÉK'}
            </p>
            {isKnown ? (
              <>
                <h3 className="text-2xl font-bold text-slate-800 dark:text-gray-100 leading-tight">
                  {lastItemDetails?.name}
                </h3>
                <div className="flex gap-4 mt-2">
                  <p className="text-slate-500 dark:text-gray-400 text-sm">
                    Beszerzési: {lastItemDetails?.netPrice.toLocaleString()} Ft
                  </p>
                  <p
                    className={`${theme.text} dark:${theme.text.replace('text-', 'text-').replace('-600', '-400')} font-bold text-lg`}
                  >
                    Eladási:{' '}
                    {lastItemDetails?.sellingPrice
                      ? lastItemDetails.sellingPrice.toLocaleString()
                      : '---'}{' '}
                    Ft
                  </p>
                </div>
              </>
            ) : (
              <div className="space-y-3 bg-white/50 dark:bg-gray-700/50 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="bg-white/80 dark:bg-gray-800/80 p-3 rounded border border-amber-300 dark:border-amber-700 text-center mb-2">
                  <span className="text-xs text-slate-500 dark:text-gray-400 block uppercase mb-1">
                    Beolvasott Kód:
                  </span>
                  <span className="text-xl font-mono font-bold text-slate-800 dark:text-gray-100 tracking-widest">
                    {lastScanned}
                  </span>
                </div>

                <button
                  onClick={onGenerateInternalEan}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition font-medium shadow-md active:scale-95 text-sm"
                  type="button"
                >
                  <Hash size={16} />
                  Új Kód Generálása (20...)
                </button>

                <div className="relative">
                  <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase">
                    Termék Neve
                  </label>
                  <input
                    ref={nameInputRef}
                    type="text"
                    className="w-full text-lg font-bold p-2 border-b-2 border-amber-300 dark:border-amber-600 bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    value={newName}
                    onChange={(e) => onNameChange(e.target.value)}
                    onFocus={() => {
                      if (newName.length > 2 && nameSuggestions.length > 0) {
                        // handled by parent
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        // handled by parent
                      }, 200);
                    }}
                  />
                  {showSuggestions && nameSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-card border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {nameSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => onSuggestionSelect(suggestion)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white transition"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Hybrid Pricing Input */}
                <PricingInput
                  purchaseNet={newPurchaseNet}
                  purchaseGross={newPurchaseGross}
                  sellingNet={newSellingNet}
                  sellingGross={newSellingGross}
                  vatRate={newVatRate ?? 27}
                  onPurchaseNetChange={onPurchaseNetChange}
                  onPurchaseGrossChange={onPurchaseGrossChange}
                  onSellingNetChange={onSellingNetChange}
                  onSellingGrossChange={onSellingGrossChange}
                  onVatRateChange={onVatRateChange}
                  purchaseLabel="Beszerzési Ár"
                  sellingLabel="Eladási Ár"
                />
                <div className="relative">
                  <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase">
                    Mértékegység
                  </label>
                  <select
                    value={newUnit || 'db'}
                    onChange={(e) => onUnitChange?.(e.target.value as 'db' | 'm' | 'kg')}
                    className="w-full text-lg bg-transparent border-b-2 border-slate-300 dark:border-gray-600 text-gray-900 dark:text-white font-bold py-2 outline-none appearance-none cursor-pointer pr-6"
                  >
                    <option value="db" className="bg-card text-gray-900 dark:text-white">
                      db
                    </option>
                    <option value="m" className="bg-card text-gray-900 dark:text-white">
                      m
                    </option>
                    <option value="kg" className="bg-card text-gray-900 dark:text-white">
                      kg
                    </option>
                  </select>
                  <span className="absolute right-0 bottom-2 pointer-events-none text-slate-500 dark:text-gray-400">
                    ▼
                  </span>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase">
                    Jelzési limit ({currentUnit.toUpperCase()})
                  </label>
                  <input
                    type="number"
                    className="w-full text-lg font-bold p-2 border-b-2 border-amber-300 dark:border-amber-600 bg-transparent outline-none text-gray-900 dark:text-white"
                    value={newMinQuantity}
                    onChange={(e) => onMinQuantityChange(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                    step={isDecimalUnit ? '0.001' : '1'}
                  />
                </div>
                <div className="flex items-center gap-4 p-3 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700 mt-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="weighable"
                      checked={newIsWeighable}
                      onChange={(e) => onIsWeighableChange(e.target.checked)}
                      className="w-5 h-5 rounded border-primary text-primary focus:ring-accent"
                    />
                    <label
                      htmlFor="weighable"
                      className="text-sm font-bold text-slate-700 dark:text-gray-300 uppercase cursor-pointer"
                    >
                      Mérhető termék (súly alapú)
                    </label>
                  </div>
                  {newIsWeighable && (
                    <div className="flex-1">
                      <label className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase block mb-1">
                        Egységsúly (g)
                      </label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={newUnitWeight === '0' || newUnitWeight === '' ? '' : newUnitWeight}
                        onChange={(e) => onUnitWeightChange(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="w-full bg-transparent border-b-2 border-blue-400 dark:border-blue-500 outline-none text-lg font-bold text-blue-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quantity Input */}
          <div
            className={`flex flex-col items-center bg-white dark:bg-gray-700 p-3 rounded-xl border shadow-sm min-w-[120px] ${theme.border} ${theme.light} dark:border-gray-600`}
          >
            <label className="text-[10px] font-bold uppercase text-slate-400 dark:text-gray-400 mb-1">
              Mennyiség ({currentUnit.toUpperCase()})
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const step = isDecimalUnit ? 0.01 : 1;
                  const newVal = Math.max(0, transactionQty - step);
                  const newValStr = isDecimalUnit ? newVal.toFixed(2) : String(newVal);
                  setQuantityInputStr(newVal === 0 ? '' : newValStr);
                  onQtyChange(newValStr);
                }}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-gray-600 text-slate-500 dark:text-gray-400"
              >
                -
              </button>
              <input
                ref={qtyInputRef}
                type="text"
                inputMode={isDecimalUnit ? 'decimal' : 'numeric'}
                value={quantityInputStr}
                onChange={(e) => {
                  const v = e.target.value;
                  // Mark that user is actively typing — suppress external transactionQty syncs
                  isUserTypingQtyRef.current = true;
                  if (userTypingTimeoutRef.current) clearTimeout(userTypingTimeoutRef.current);
                  userTypingTimeoutRef.current = setTimeout(() => {
                    isUserTypingQtyRef.current = false;
                  }, 1000);

                  if (isDecimalUnit) {
                    // Allow only digits, one dot, one comma (so "0." and "0,2" work)
                    const filtered = v.replace(',', '.').replace(/[^0-9.]/g, '');
                    const parts = filtered.split('.');
                    const allowed =
                      parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : filtered;
                    setQuantityInputStr(allowed);
                    onQtyChange(allowed);
                  } else {
                    // Integer units (db): allow only digits, allow empty for typing
                    const filtered = v.replace(/[^0-9]/g, '');
                    // Remove leading zeros (e.g. "05" → "5") but keep empty string
                    const cleaned = filtered.replace(/^0+(\d)/, '$1');
                    setQuantityInputStr(cleaned);
                    onQtyChange(cleaned);
                  }
                }}
                onFocus={(e) => {
                  // Select all text on focus so user can immediately overwrite
                  e.target.select();
                }}
                placeholder="0"
                className={`w-16 text-center text-3xl font-extrabold bg-transparent outline-none ${theme.text} dark:text-white`}
              />
              <button
                onClick={() => {
                  const step = isDecimalUnit ? 0.01 : 1;
                  const newVal = transactionQty + step;
                  const newValStr = isDecimalUnit ? newVal.toFixed(2) : String(newVal);
                  setQuantityInputStr(newValStr);
                  onQtyChange(newValStr);
                }}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-gray-600 text-slate-500 dark:text-gray-400"
              >
                +
              </button>
            </div>
            <div className="text-xs text-slate-400 dark:text-gray-400 mt-1 pt-1 border-t border-slate-200 dark:border-gray-600 w-full text-center">
              Készleten: {lastItemQty} {currentUnit}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onDiscard}
            className="flex flex-col items-center justify-center p-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 transition active:scale-95"
          >
            <Trash2 size={24} className="mb-1" />
            <span className="text-xs font-bold uppercase">Visszavonás</span>
          </button>
          {isKnown ? (
            <button
              onClick={onApprove}
              className={`flex flex-col items-center justify-center p-3 rounded-lg text-white shadow-md transition active:scale-95 ${theme.bg} ${theme.hover} ${theme.shadow}`}
            >
              <ArrowRight size={24} className="mb-1" />
              <span className="text-xs font-bold uppercase">
                {scanMode === 'SALE' ? 'KOSÁRBA' : 'RÖGZÍTÉS'}
              </span>
            </button>
          ) : (
            <button
              onClick={onSave}
              disabled={!newName}
              className="flex flex-col items-center justify-center p-3 rounded-lg bg-green-600 text-white hover:bg-green-700 shadow-md transition active:scale-95 disabled:opacity-50"
            >
              <Save size={24} className="mb-1" />
              <span className="text-xs font-bold uppercase">Rögzítés</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
